import { addMonths, format, parse } from 'date-fns';
import { ko } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  Button,
  Calendar,
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@herit-fe/heritage-ui';
import { UI_TEXT } from '@/shared/languages';
import { DrawerContent } from './drawer-content';
import { DATE_TRIGGER_CLASS } from './form-control-style';
import { MonthGrid } from './month-grid';
import { useIsCompact } from './use-is-compact';

export interface DateFieldProps {
  /** 트리거 id — `FieldLabel htmlFor` 와 연결(접근성). */
  id?: string;
  /**
   * 필드명(= `FieldLabel` 문구) — **compact 드로워 헤더**로 쓰인다(오버레이가 라벨을 덮으므로 맥락 유지용).
   * 생략 시 기본 문구로 폴백. (`SelectField.label` 과 동일 관행.)
   */
  label?: string;
  /** 선택값 `yyyy-MM-dd`. */
  value: string;
  /** 선택 시 `yyyy-MM-dd` 로 통지. */
  onChange: (ymd: string) => void;
  /** 선택 가능 하한 `yyyy-MM-dd`(이전 비활성). */
  min?: string;
  /** 선택 가능 상한 `yyyy-MM-dd`(이후 비활성). */
  max?: string;
  /** placeholder(미선택 시 트리거 텍스트, 기본 '날짜 선택'). */
  placeholder?: string;
}

const ISO_DAY = 'yyyy-MM-dd';
const ISO_MONTH = 'yyyy-MM'; // 월 뷰(`MonthGrid`) 입출력 — `MonthField` 와 동일 형식
const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/; // `MonthField` 의 `ISO_MONTH.test` 가드 패턴과 동형 — 형식 불일치 시 parse 건너뜀
// 표시: `2026-06-17` — 저장 형식(`ISO_DAY`)과 **같은 문자열**. 트리거에 보이는 값이 곧 조회조건 칩·엑셀로
// 나가는 값이라 둘을 갈라 둘 이유가 없다(요일 `(EEE)` 접미는 그래서 뗐다 — 칩 폭만 먹고 정보는 안 는다).
// native `<input type=date>` 를 안 쓰는 이유는 그대로 — 브라우저 기본 위젯의 폰트·크기를 맞출 수 없다.
const DISPLAY = ISO_DAY;
// 캡션 표시: `2026년 6월` — rdp 기본 캡션(ko locale)과 같은 문구라 자체 헤더로 바꿔도 표기가 안 달라진다.
const CAPTION = 'yyyy년 M월';

/**
 * rdp 기본 헤더 제거 — 캡션·nav 를 자체 `header` 로 대체하므로 원본은 숨긴다. heritage 는 `classNames` 를 **키 단위로
 * 덮어쓰므로**(병합 아님) `'hidden'` 만 주면 원래 레이아웃 클래스까지 함께 사라진다 — 여기선 그게 의도.
 */
const HIDE_RDP_HEADER = { nav: 'hidden', month_caption: 'hidden' } as const;

/**
 * 달력 폭·행높이 — **폭은 채우되 행 높이는 고정**. heritage `Calendar` 의 날짜 칸(`td`)은
 * `aspect-square h-full w-full` 이라 **높이가 폭을 따라간다**. 그래서 `w-full` 만 주면 넓어진 만큼 세로도
 * 늘어나 달력이 화면을 잡아먹는다. 날짜 **버튼**에 `max-h-*` 를 걸어도 소용없다 — 정사각인 주체는 td 라
 * 행 높이가 그대로고 버튼만 칸 안에서 레터박스처럼 갇힌다.
 *
 * 그래서 td·버튼 양쪽의 `aspect-square` 를 풀고(`aspect-auto`) 높이를 `--cell-size` 로 **직접** 지정한다.
 * 세로 크기 조절은 이 변수 한 줄이 SSOT(heritage 기본 = `--spacing(8)`).
 *
 * ⚠️ `**:[data-day]:…` 류의 **원시 속성 셀렉터는 쓰지 말 것** — Tailwind v4 가 `[data-day]` 를 엘리먼트 이름으로
 * 파싱해 `:is(data-day)`(존재하지 않는 태그)를 뱉는다. 클래스는 DOM 에 붙는데 규칙이 안 먹어 디버깅이 어렵다.
 * (`scroll-area.tsx` 에 같은 함정 기록.) 태그 셀렉터(`[&_td]`)는 정상 컴파일된다.
 */
const CALENDAR_CLASS =
  'w-full [--cell-size:--spacing(10)] [&_td]:aspect-auto [&_td]:h-(--cell-size) [&_td_button]:aspect-auto [&_td_button]:h-full';

/**
 * 날짜 선택 — heritage `Calendar`(react-day-picker). `yyyy-MM-dd` 문자열을 입출력하고 트리거에
 * `yyyy-MM-dd (요일)` 을 표시한다. native `<input type=date>`(브라우저 기본 UI·폰트 비대·요일 부재) 대체.
 * 라벨은 호출처가 `Field`/`FieldLabel`(htmlFor↔id)로 연결.
 *
 * **표면은 반응형**(경계 `lg`, `useIsCompact`) — pc 는 `Popover`, 비-pc 는 bottom `Drawer`. 달력 본체는 두 표면이
 * 공유하므로(`renderCalendar`) 동작·제약(min/max)이 갈리지 않는다. (`SelectField` 와 동일 전략 — ADR ⑤-1.)
 *
 * **월 점프** — 캡션의 년월을 누르면 12개월 그리드(`MonthGrid`, `MonthField` 와 공용)가 **같은 종류의 표면으로 하나
 * 더** 열려 먼 달로 건너뛴다(월 화살표 연타 회피). 월을 고르면 **선택이 아니라 표시 월만** 바꾸고 닫힌다 — 이 필드의
 * 값은 어디까지나 '일'이라, 월 선택이 값 확정으로 새면 안 된다. 그래서 표시 월(`month`)을 controlled 로 들고 있다.
 */
export function DateField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  placeholder = UI_TEXT.DATE_PLACEHOLDER,
}: DateFieldProps) {
  const isCompact = useIsCompact();
  const [open, setOpen] = useState(false);
  const selected = ISO_DAY_RE.test(value) ? parse(value, ISO_DAY, new Date()) : undefined;
  const minDate = min ? parse(min, ISO_DAY, new Date()) : undefined;
  const maxDate = max ? parse(max, ISO_DAY, new Date()) : undefined;

  const [monthOpen, setMonthOpen] = useState(false);
  const [month, setMonth] = useState<Date>(() => selected ?? new Date());
  // 월 점프 표면의 연도 커서 — **표시 월(`month`)과 분리**한다. 연도 네비는 고를 후보를 훑는 동작일 뿐이라
  // 그 자체로는 아무것도 확정하지 않는다(뒤 달력이 따라 움직이면 안 됨). 반영은 월을 골랐을 때 한 번.
  const [jumpYear, setJumpYear] = useState(() => month.getFullYear());

  // 월 표면을 열 때마다 연도 커서를 현재 표시 월에 맞춘다(딴 연도 훑다 닫았어도 재진입 시 제자리).
  const handleMonthOpenChange = (next: boolean) => {
    setMonthOpen(next);
    if (next) setJumpYear(month.getFullYear());
  };

  // 열 때마다 선택값 기준으로 초기화 — 다른 달을 보다 닫았어도 재진입 시 선택 날짜가 보이고, 월 표면을 띄운 채
  // 닫았어도 다음엔 달력부터 시작한다(`MonthField.handleOpenChange` 와 동일 관행).
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setMonthOpen(false);
      setMonth(selected ?? new Date());
    }
  };

  /**
   * 캡션 행(◀ 년월 ▶)은 **rdp 것을 끄고 직접 그린다**(`HIDE_RDP_HEADER`). rdp 가 그리는 캡션 안에 팝오버 트리거를
   * 심으면 두 가지가 걸린다 — ① heritage nav 가 `absolute inset-x-0` 로 헤더 행 전체를 덮어 캡션 클릭이 막히고
   * (화살표만 동작), ② 트리거가 rdp 의 재렌더 대상이라 base-ui 가 앵커를 처음 한 번 잘못 잡는다(첫 열림만 위치
   * 어긋남). 트리거를 평범한 형제 요소로 꺼내면 둘 다 원인 자체가 없어진다. 표시 월은 이미 controlled(`month`)라
   * 화살표도 우리가 그대로 굴릴 수 있다.
   */
  const CaptionTrigger = isCompact ? DrawerTrigger : PopoverTrigger;
  const monthKey = format(month, ISO_MONTH);
  const header = (
    <div className="flex items-center justify-between pb-2">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        disabled={Boolean(min && monthKey <= min.slice(0, 7))}
        onClick={() => setMonth(addMonths(month, -1))}
        aria-label={UI_TEXT.DATE_PREV_MONTH}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <CaptionTrigger className="flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium hover:bg-accent">
        {format(month, CAPTION, { locale: ko })}
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </CaptionTrigger>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        disabled={Boolean(max && monthKey >= max.slice(0, 7))}
        onClick={() => setMonth(addMonths(month, 1))}
        aria-label={UI_TEXT.DATE_NEXT_MONTH}
      >
        <ChevronRight className="size-4" />
      </Button>
    </div>
  );

  const triggerLabel = selected ? format(selected, DISPLAY, { locale: ko }) : placeholder;
  const triggerBody: ReactNode = (
    <>
      <span>{triggerLabel}</span>
      <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
    </>
  );

  // 월 점프 그리드 — **월을 골랐을 때만** 표시 월을 옮기고 닫는다(연도 네비는 커서만 이동, 값 확정도 아님).
  // min/max 는 일 단위 제약을 `yyyy-MM` 로 절삭 — 월의 하루라도 선택 가능하면 그 월은 열어야 한다.
  const monthGrid = (
    <MonthGrid
      year={jumpYear}
      onYearChange={setJumpYear}
      selected={selected ? format(selected, ISO_MONTH) : undefined}
      min={min?.slice(0, 7)}
      max={max?.slice(0, 7)}
      onSelect={(ym) => {
        setMonth(parse(ym, ISO_MONTH, new Date()));
        setMonthOpen(false);
      }}
    />
  );

  // 달력 본체는 두 표면 공유 — 폭 정책만 주입한다(pc 팝오버=자연폭, compact 드로워=폭 채움+행높이 고정).
  // 캡션·nav 는 위 `header` 가 대신하므로 rdp 쪽은 숨기고, 자체 `p-3` 도 껍데기(아래 `renderBody`)로 옮긴다.
  const renderCalendar = (compact: boolean) => (
    <Calendar
      mode="single"
      selected={selected}
      month={month}
      onMonthChange={setMonth}
      onSelect={(day) => {
        if (!day) return;
        onChange(format(day, ISO_DAY));
        setOpen(false);
      }}
      disabled={(date) => Boolean((minDate && date < minDate) || (maxDate && date > maxDate))}
      locale={ko}
      classNames={HIDE_RDP_HEADER}
      className={`p-0${compact ? ` ${CALENDAR_CLASS}` : ''}`}
      autoFocus
    />
  );

  /**
   * 본체 = 달력 + **월 표면 Root**. 월 점프는 달력을 갈아끼우지 않고 날짜 표면과 **똑같은 표면을 하나 더** 띄운다
   * (pc=Popover / compact=Drawer) — 표면 종류가 날짜 선택과 같아 위치·애니메이션·닫기 동작이 이미 익숙한 그대로다.
   * Root 가 헤더+달력을 감싸므로 캡션의 `CaptionTrigger` 가 context 로 이 Root 에 붙는다.
   */
  const renderBody = (compact: boolean) =>
    compact ? (
      <Drawer open={monthOpen} onOpenChange={handleMonthOpenChange} direction="bottom">
        <div className="p-3">
          {header}
          {renderCalendar(true)}
        </div>
        <DrawerContent aria-describedby={undefined} className="gap-0 overflow-hidden">
          <DrawerHeader className="shrink-0 border-b border-border">
            <DrawerTitle className="text-base font-semibold">
              {UI_TEXT.DATE_MONTH_JUMP_TITLE}
            </DrawerTitle>
          </DrawerHeader>
          <div className="p-4">{monthGrid}</div>
        </DrawerContent>
      </Drawer>
    ) : (
      <Popover open={monthOpen} onOpenChange={handleMonthOpenChange}>
        <div className="p-3">
          {header}
          {renderCalendar(false)}
        </div>
        <PopoverContent className="w-auto p-3 bg-card" align="center">
          {monthGrid}
        </PopoverContent>
      </Popover>
    );

  if (isCompact) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange} direction="bottom">
        <DrawerTrigger
          id={id}
          className={DATE_TRIGGER_CLASS}
          data-placeholder={selected ? undefined : ''}
        >
          {triggerBody}
        </DrawerTrigger>
        <DrawerContent aria-describedby={undefined} className="gap-0 overflow-hidden">
          <DrawerHeader className="shrink-0 border-b border-border">
            <DrawerTitle className="text-base font-semibold">
              {label ?? UI_TEXT.DATE_PLACEHOLDER}
            </DrawerTitle>
          </DrawerHeader>
          {renderBody(true)}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        id={id}
        className={DATE_TRIGGER_CLASS}
        data-placeholder={selected ? undefined : ''}
      >
        {triggerBody}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-card" align="start">
        {renderBody(false)}
      </PopoverContent>
    </Popover>
  );
}
